import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Save, Send, ChevronDown } from "lucide-react";

export default function WeeklyEducationStudio() {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);

    const load = () => api.get("/admin/weekly-education").then(r => setData(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const save = async (week) => {
        setSaving(true);
        try {
            await api.put(`/admin/weekly-education/${week}`, drafts[week] || {});
            toast.success(`Week ${week} saved.`);
            load();
        } catch { toast.error("Couldn't save."); }
        setSaving(false);
    };

    const sendDue = async () => {
        setSending(true);
        try {
            const r = await api.post("/admin/weekly-education/send-due");
            toast.success(`Sent ${r.data.sent} email(s).`);
            load();
        } catch { toast.error("Send failed."); }
        setSending(false);
    };

    if (!data) return <div className="ml-card p-6 mt-8 flex items-center gap-2 text-[#6E7A74]" data-testid="weekly-edu-loading"><Loader2 size={15} className="animate-spin" /> Loading weekly education series…</div>;

    return (
        <section className="mt-10" data-testid="weekly-education-studio">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-heading font-bold text-xl text-[#1F2624] flex items-center gap-2">
                        <Mail size={18} className="text-teal-dark" /> Weekly education series (8 weeks)
                    </h2>
                    <p className="text-sm text-[#6E7A74]">One educational email per week for a caregiver's first eight weeks. Sends automatically twice daily. {data.note}</p>
                </div>
                <button onClick={sendDue} disabled={sending} data-testid="weekly-edu-send-due"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-dark hover:underline disabled:opacity-50">
                    <Send size={14} /> {sending ? "Sending…" : "Send due now"}
                </button>
            </div>
            <div className="mt-4 space-y-2">
                {data.weeks.map((w) => {
                    const d = drafts[w.week] || {};
                    const isOpen = open === w.week;
                    return (
                        <div key={w.week} className="ml-card p-4" data-testid={`weekly-edu-week-${w.week}`}>
                            <button onClick={() => setOpen(isOpen ? null : w.week)} className="w-full flex items-center justify-between gap-3 text-left" data-testid={`weekly-edu-toggle-${w.week}`}>
                                <span className="text-sm font-medium text-[#1F2624]">
                                    <span className="text-teal-dark font-heading font-bold mr-2">Week {w.week}</span>
                                    {w.subject}
                                </span>
                                <span className="flex items-center gap-3 shrink-0">
                                    <span className="text-xs text-[#6E7A74]">{w.sent_count} sent</span>
                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-2xl ${w.enabled ? "bg-sage-light text-sage-dark" : "bg-cream-dark text-[#6E7A74]"}`}>{w.enabled ? "on" : "off"}</span>
                                    <ChevronDown size={15} className={isOpen ? "rotate-180 transition-transform" : "transition-transform"} />
                                </span>
                            </button>
                            {isOpen && (
                                <div className="mt-3 space-y-2.5">
                                    <input className="ml-input !min-h-[44px] text-sm" value={d.subject ?? w.subject}
                                        onChange={(e) => setDrafts(v => ({ ...v, [w.week]: { ...v[w.week], subject: e.target.value } }))}
                                        data-testid={`weekly-edu-subject-${w.week}`} />
                                    <textarea rows={7} className="ml-input text-sm font-mono !min-h-[140px]" value={d.body_html ?? w.body_html}
                                        onChange={(e) => setDrafts(v => ({ ...v, [w.week]: { ...v[w.week], body_html: e.target.value } }))}
                                        data-testid={`weekly-edu-body-${w.week}`} />
                                    <div className="flex items-center gap-3">
                                        <button onClick={() => save(w.week)} disabled={saving} data-testid={`weekly-edu-save-${w.week}`}
                                            className="inline-flex items-center gap-1.5 rounded-2xl bg-teal text-white px-4 py-2 text-sm font-semibold hover:bg-teal-dark disabled:opacity-50">
                                            <Save size={13} /> Save
                                        </button>
                                        <label className="inline-flex items-center gap-2 text-sm text-[#4C5551]">
                                            <input type="checkbox" checked={d.enabled ?? w.enabled}
                                                onChange={(e) => { setDrafts(v => ({ ...v, [w.week]: { ...v[w.week], enabled: e.target.checked } })); }}
                                                data-testid={`weekly-edu-enabled-${w.week}`} />
                                            Enabled
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
